import ShortCourseApi from '@web/api/modules/ShortCourse'
import Logger from '@web/common/Logger'
import tags from '@web/consts/tags'
import {
  IShortCourse,
  IShortCourseTag,
  IShortCourseFilters,
  ICacheEntry,
  ShortCourseState,
  ShortCourseGettersTree,
  ShortCourseMutationsTree,
  ShortCourseActionsTree
} from '@web/store/types/modules/shortCourse'

const state: ShortCourseState = {
  courses: {},
  filters: null,
  activeTags: [],
  searchQuery: {
    textSearch: '',
    tagId: undefined,
    cids: [],
    page: 1,
    perPage: 8
  },
  pagination: {
    currentPage: 1,
    perPage: 8,
    totalCount: 0,
    pageCount: 0
  },
  isSearching: false,
  isLoaded: false,
  error: null,
  searchCache: {}
}

const getters: ShortCourseGettersTree = {
  getCourses: (state) => state.courses,
  getAllCoursesArray: (state) => Object.values(state.courses),
  getCourseById: (state) => (id: number) => state.courses[id],
  getCourseByIds: (state) => (ids: number[]) =>
    ids.map(id => state.courses[id]).filter((course): course is IShortCourse => course !== undefined),
  getCoursesByTags: (state) => {
    function getTagKey (tag: IShortCourseTag) {
      return `${tag.sort || 999}-${tag.id}`
    }
    const coursesByTags: Record<string, { tag: IShortCourseTag; courses: IShortCourse[] }> = {}

    const sortedTags = [...state.activeTags].sort((a, b) => (a.sort || 999) - (b.sort || 999))
    sortedTags.forEach(tag => {
      coursesByTags[getTagKey(tag)] = {
        tag,
        courses: Object.values(state.courses).filter(course => course.tags.some(t => t.id === tag.id || t.name === tag.name))
      }
    })
    return coursesByTags
  },
  getCoursesByTag: (state) => (tagId: number) =>
    Object.values(state.courses).filter(course => {
      const allTags = state.filters?.tags
      const currentTag = allTags?.find(tag => tag.id === tagId)
      return course.tags.some(tag => tag.id === currentTag?.id || tag.name === currentTag?.name)
    }),
  getFilters: (state) => state.filters,
  getTags: (state) => state.filters?.tags ? [...state.filters.tags].sort((a, b) => (a.sort || 999) - (b.sort || 999)) : [],
  getActiveTags: (state) => state.activeTags ? [...state.activeTags].sort((a, b) => (a.sort || 999) - (b.sort || 999)) : [],
  getSearchQuery: (state) => state.searchQuery,
  getPagination: (state) => state.pagination,
  getIsSearching: (state) => state.isSearching,
  getIsLoaded: (state) => state.isLoaded,
  getError: (state) => state.error,
  getCachedSearch: (state) => (textSearch?: string): ICacheEntry | undefined => {
    if (!textSearch || textSearch.trim() === '') {
      return undefined
    }
    return state.searchCache[textSearch.trim()]
  }
}

const mutations: ShortCourseMutationsTree = {
  setCourses (state, payload) {
    const coursesObj: { [id: number]: IShortCourse } = {}
    payload.forEach(course => {
      coursesObj[course.id] = course
    })
    state.courses = {
      ...state.courses,
      ...coursesObj
    }
  },

  setCourse (state, payload) {
    const course = state.courses[payload.id] || {}
    state.courses[payload.id] = {
      ...course,
      ...payload
    }
  },

  removeCourse (state, payload) {
    const { [payload]: removed, ...rest } = state.courses
    state.courses = rest
  },

  setFilters (state, payload) {
    state.filters = {
      ...state.filters,
      ...payload,
      tags: payload.tags.map(tag => {
        const defaultTag = tags[tag.id as keyof typeof tags]
        return {
          ...tag,
          icon: tag.icon || defaultTag?.icon || '',
          sort: tag.sort || defaultTag?.sort || 999
        }
      })
    }
  },

  setActiveTags (state, payload) {
    const newActiveTags = payload.map(tag => {
      const defaultTag = tags[tag.id as keyof typeof tags]
      return {
        ...tag,
        icon: tag.icon || defaultTag?.icon || '',
        sort: tag.sort || defaultTag?.sort || 999
      }
    })
    const allTags = [...state.activeTags, ...newActiveTags]
    // Удаляем дубликаты по id
    state.activeTags = allTags.filter((tag, index, self) =>
      index === self.findIndex(c => c.id === tag.id)
    )
  },

  setSearchQuery (state, payload) {
    state.searchQuery = payload
  },

  setPagination (state, payload) {
    state.pagination = payload
  },

  setIsSearching (state, payload) {
    state.isSearching = payload
  },

  setIsLoaded (state, payload) {
    state.isLoaded = payload
  },

  setError (state, payload) {
    state.error = payload
  },

  setCacheEntry (state, payload) {
    state.searchCache = {
      ...state.searchCache,
      [payload.key]: payload.entry
    }
  },

  removeCacheEntry (state, payload) {
    const { [payload]: removed, ...rest } = state.searchCache
    state.searchCache = rest
  },

  clearSearchCache (state) {
    state.searchCache = {}
    Logger.info('Search cache cleared')
  },

  logout (state) {
    state.courses = {}
    state.filters = null
    state.activeTags = []
    state.searchQuery = {
      textSearch: '',
      tagId: undefined,
      cids: [],
      page: 1,
      perPage: 8
    }
    state.pagination = {
      currentPage: 1,
      perPage: 8,
      totalCount: 0,
      pageCount: 0
    }
    state.isSearching = false
    state.isLoaded = false
    state.error = null
    state.searchCache = {}
  }
}

/**
 * Генерирует ключ кэша на основе текстового поиска
 */
function generateCacheKey (textSearch?: string): string | null {
  if (!textSearch || textSearch.trim() === '') {
    return null
  }
  return textSearch.trim()
}

const actions: ShortCourseActionsTree = {
  /**
   * Поиск курсов с сохранением параметров запроса
   */
  async search ({ state, commit }, payload) {
    const {
      textSearch,
      tagId,
      cids = [],
      page = 1,
      perPage = 8,
      clearPrevious = false,
      forceRefresh = false
    } = payload

    commit('setIsSearching', true)
    commit('setError', null)

    // Сохраняем параметры запроса
    commit('setSearchQuery', { textSearch, tagId, cids, page, perPage })

    // Генерируем ключ кэша (только по textSearch)
    const cacheKey = generateCacheKey(textSearch)

    try {
      let data: { courses: IShortCourse[]; filters?: IShortCourseFilters }
      let headers: Headers

      // Проверяем наличие данных в кэше (если есть cacheKey и не forceRefresh)
      const cachedEntry = cacheKey && !forceRefresh ? state.searchCache[cacheKey] : undefined

      if (cachedEntry) {
        // Используем данные из кэша
        Logger.info('Using cached search results for:', cacheKey)
        data = cachedEntry.data
        headers = cachedEntry.headers
      } else {
        // Выполняем API запрос
        if (forceRefresh && cacheKey) {
          Logger.info('Force refreshing search results for:', cacheKey)
        }

        const response = await ShortCourseApi.search({
          textSearch,
          tagId,
          cids,
          page,
          perPage
        })

        data = response.data
        headers = response.headers

        // Сохраняем результат в кэш (только если есть textSearch)
        if (cacheKey) {
          commit('setCacheEntry', {
            key: cacheKey,
            entry: {
              data,
              headers,
              timestamp: Date.now()
            }
          })
          Logger.info('Search results cached for:', cacheKey)
        }
      }

      // Если clearPrevious = true, очищаем предыдущие результаты
      if (clearPrevious) {
        commit('setCourses', [])
      }

      // Сохраняем фильтры
      if (data.filters) {
        commit('setFilters', data.filters)
      }

      // Сохраняем активные категории | Объявляем список активных категорий
      const activeTags: IShortCourseTag[] = []

      // Сохраняем курсы (учитываем дубли)
      data.courses.forEach(course => {
        commit('setCourse', course)

        course.tags.forEach(tag => {
          if (tag.id && tag.name && !activeTags.some(t => t.id === tag.id)) {
            activeTags.push(tag)
          }
        })
      })

      // Сохраняем активные категории | Записываем список активных категорий
      commit('setActiveTags', activeTags)

      commit('setPagination', {
        currentPage: parseInt(headers.get('x-pagination-current-page') || '1', 10),
        pageCount: parseInt(headers.get('x-pagination-page-count') || '0', 10),
        perPage: parseInt(headers.get('x-pagination-per-page') || '8', 10),
        totalCount: parseInt(headers.get('x-pagination-total-count') || '0', 10)
      })

      commit('setIsLoaded', true)
      return data
    } catch (error) {
      Logger.error('Failed to search courses:', error)
      commit('setError', 'Failed to search courses')
      throw error
    } finally {
      commit('setIsSearching', false)
    }
  },

  async searchByCourseId ({ state, getters, commit }, { courseId, force = false }) {
    if (getters.getCourseById(courseId) && !force) {
      return Promise.resolve(getters.getCourseById(courseId))
    }

    return ShortCourseApi.search({
      cids: [courseId],
      page: 1,
      perPage: 1
    })
      .then(response => {
        const { courses } = response.data
        return courses[0]
      })
      .then(course => {
        commit('setCourse', course)
        const activeTags: IShortCourseTag[] = []

        course.tags.forEach(tag => {
          if (tag.id && tag.name && !activeTags.some(t => t.id === tag.id)) {
            activeTags.push(tag)
          }
        })
        commit('setActiveTags', activeTags)
        return course
      })
  },

  /**
   * Поиск с текущими параметрами (для обновления)
   */
  async refreshSearch (context) {
    return await context.dispatch('search', {
      ...context.state.searchQuery,
      clearPrevious: true,
      forceRefresh: true
    })
  },

  /**
   * Загрузить следующую страницу результатов
   */
  async loadNextPage (context) {
    const nextPage = context.state.searchQuery.page + 1
    return await context.dispatch('search', {
      ...context.state.searchQuery,
      page: nextPage,
      clearPrevious: false
    })
  },

  /**
   * Поиск с новыми параметрами (очищает предыдущие результаты)
   */
  async searchWithNewParams (context, payload) {
    return await context.dispatch('search', {
      ...payload,
      clearPrevious: true
    })
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as any
